import { useOkapiKy, useStripes } from '@folio/stripes-core';
import { useQuery } from 'react-query';

const TRANSLATORS_API = 'languageTranslators';

/**
 * Fetches language translators and determines if current user
 * is an assigned translator for a given locale.
 *
 * Returns:
 *  translators     — raw array from API
 *  isAssignedTranslator(localeValue) — bool
 *  getLocaleTranslators(localeValue) — [userId, ...]
 */
export function useLanguageTranslators({ enabled = true } = {}) {
  const ky = useOkapiKy();
  const stripes = useStripes();
  const currentUserId = stripes?.user?.user?.id;

  const { data, ...rest } = useQuery(
    [TRANSLATORS_API],
    () =>
      ky
        .get(TRANSLATORS_API, {
          searchParams: { query: 'cql.allRecords=1 sortby localeValue', limit: 1000 },
        })
        .json(),
    {
      enabled,
      staleTime: 300_000,
    },
  );

  const translators = data?.languageTranslators || [];

  /**
   * Returns list of userIds assigned to a locale.
   */
  const getLocaleTranslators = (localeValue) => {
    const record = translators.find((t) => t.localeValue === localeValue);
    if (!record?.translators) return [];
    return record.translators.map((t) =>
      typeof t === 'string' ? t : t.value || t.id || t
    );
  };

  /**
   * Returns true if the current user is assigned as translator
   * for the given localeValue.
   */
  const isAssignedTranslator = (localeValue) => {
    if (!currentUserId || !localeValue) return false;
    return getLocaleTranslators(localeValue).includes(currentUserId);
  };

  return {
    translators,
    isAssignedTranslator,
    getLocaleTranslators,
    ...rest,
  };
}
